import { useState } from "preact/hooks";
import type { PreChatFormConfig } from "../hooks/useConfig.js";
import { BotIcon } from "./BotIcon.js";
import { t } from "../lib/i18n.js";
import { isValidPhone, isBlockedEmailDomain } from "../lib/validation.js";

const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

interface PreChatFormProps {
  config: PreChatFormConfig;
  botName: string;
  language: string;
  blockedEmailDomains?: string[];
  onSubmit: (data: Record<string, string>) => void;
}

export function PreChatForm({ config, botName, language, blockedEmailDomains, onSubmit }: PreChatFormProps) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const field of config.fields) {
      init[field.name] = "";
    }
    return init;
  });
  // Field name -> i18n key of the validation error (absent = valid)
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  function validateField(type: string, value: string): string | undefined {
    if (type === "email") {
      if (!EMAIL_RE.test(value)) return "prechat.invalidEmail";
      if (isBlockedEmailDomain(value, blockedEmailDomains)) return "prechat.blockedEmail";
    }
    if (type === "tel" && !isValidPhone(value)) return "prechat.invalidPhone";
    return undefined;
  }

  function handleSubmit(e: Event) {
    e.preventDefault();

    const newErrors: Record<string, string | undefined> = {};
    let hasError = false;
    for (const field of config.fields) {
      const value = values[field.name]?.trim() ?? "";
      if (field.required && !value) {
        newErrors[field.name] = "prechat.required";
        hasError = true;
      } else if (value) {
        const err = validateField(field.type, value);
        if (err) {
          newErrors[field.name] = err;
          hasError = true;
        }
      }
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    // Filter out empty values
    const data: Record<string, string> = {};
    for (const [key, val] of Object.entries(values)) {
      if (val.trim()) {
        data[key] = val.trim();
      }
    }
    onSubmit(data);
  }

  return (
    <div class="flex flex-col h-full">
      {/* Scroll container + min-h-full inner: centers when content is short,
          scrolls from the top when it overflows. justify-center directly on an
          overflowing flex container clips both ends — with 4 fields the skip
          button fell outside the 600px window and was unreachable. */}
      <div class="flex-1 overflow-y-auto px-6 py-8">
        <div class="min-h-full flex flex-col items-center justify-center">
        <div class="size-12 rounded-full bg-[var(--primary)] flex items-center justify-center mb-4">
          <BotIcon class="size-6 text-[var(--primary-foreground)]" />
        </div>
        <h2 class="text-base font-semibold text-[var(--foreground)] text-center">
          {config.title}
        </h2>
        <p class="text-sm text-[var(--muted-foreground)] text-center mt-1 mb-6">
          {config.subtitle}
        </p>

        <form onSubmit={handleSubmit} class="w-full max-w-[280px] space-y-3">
          {config.fields.map((field) => (
            <div key={field.name}>
              <label class="block text-xs font-medium text-[var(--foreground)] mb-1">
                {field.label}
                {field.required && (
                  <span class="text-red-500 ml-0.5">*</span>
                )}
              </label>
              <input
                type={field.type}
                value={values[field.name] || ""}
                onInput={(e) => {
                  const val = (e.target as HTMLInputElement).value;
                  setValues((prev) => ({ ...prev, [field.name]: val }));
                  if (errors[field.name]) {
                    setErrors((prev) => ({ ...prev, [field.name]: undefined }));
                  }
                }}
                class={`w-full rounded-lg border px-3 py-2 text-sm bg-[var(--background)] text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] ${
                  errors[field.name]
                    ? "border-red-500"
                    : "border-[var(--input)]"
                }`}
                placeholder={field.label}
              />
              {errors[field.name] && (
                <p class="mt-1 text-[11px] text-red-500">{t(errors[field.name]!, language)}</p>
              )}
            </div>
          ))}

          <button
            type="submit"
            class="w-full rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] py-2.5 text-sm font-medium hover:opacity-90 active:scale-[0.98] transition-all duration-150 mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-1"
          >
            {t("prechat.submit", language, { botName })}
          </button>
          {config.allowSkip !== false && (
            <button
              type="button"
              onClick={() => onSubmit({})}
              class="w-full text-center text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors mt-2 py-1"
            >
              {t("prechat.skip", language)}
            </button>
          )}
        </form>
        </div>
      </div>
    </div>
  );
}
